import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { format } from 'date-fns';

export default function useActivePromos() {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    getDocs(collection(db, 'promotions'))
      .then((snap) => {
        setPromos(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => p.active)
        );
      })
      .catch(() => {});
  }, []);

  return promos;
}

/**
 * Find the best matching promo for a space + pricing type.
 *
 * Two date checks:
 *   1. Purchase window — today must be within purchaseStart..purchaseEnd (can the customer buy it now?)
 *   2. Applicable dates — bookingStartDate must fall within at least one applicableDates range
 *
 * For display purposes (homepage, spaces page) where no bookingStartDate is provided,
 * we only check the purchase window.
 */
export function findPromo(promos, spaceId, pricingType, bookingStartDate, now) {
  const today = format(now || new Date(), 'yyyy-MM-dd');

  return promos.find((p) => {
    // Space + pricing match
    if (p.spaceId !== 'all' && p.spaceId !== spaceId) return false;
    if (p.pricingType !== 'all' && p.pricingType !== pricingType) return false;

    // Purchase window check (must be purchasable today)
    const purchaseStart = p.purchaseStart || p.startDate;
    const purchaseEnd = p.purchaseEnd || p.endDate;
    if (purchaseStart > today || purchaseEnd < today) return false;

    // If no booking date provided (display only), purchase window is enough
    if (!bookingStartDate) return true;

    // Booking date must fall within at least one applicable date range
    const bookingDate = format(
      bookingStartDate instanceof Date ? bookingStartDate : new Date(bookingStartDate),
      'yyyy-MM-dd'
    );
    const ranges = p.applicableDates || [{ start: p.startDate, end: p.endDate }];
    return ranges.some((r) => r.start <= bookingDate && r.end >= bookingDate);
  }) || null;
}

/**
 * Calculate the discounted price.
 */
export function applyPromo(promo, originalPrice) {
  if (!promo) return originalPrice;
  if (promo.discountType === 'percent') {
    const pct = Math.min(promo.discountValue, 100); // cap at 100%
    return Math.max(0, Math.round(originalPrice * (100 - pct) / 100));
  }
  return Math.max(0, originalPrice - promo.discountValue);
}
